// Hotel-Edition switch and capability contract (PLAN.md §2, owner A0). ONE source for "is this company a
// hotel" (the same rule as the strategy gate) and for which hotel modules the running server offers.
// Menu and route locks hang off `edition.modules` from GET /api/companies/me, so a code rollback removes
// modules automatically and switching a module on is an env change (HOTEL_EDITION_MODULES) with Mike's go.
import { resolveDfyChannel } from "./dfyChannel.js";

export type EditionKind = "hotel" | "agency";

/** The hotel module vocabulary (names and types). Which of them a release has BUILT is server-side
 *  (server/config/editionModules.ts); a module named in the env but unknown here is dropped, so an env
 *  that runs ahead of the deployed code never unlocks a page the server cannot serve. */
export const EDITION_MODULES = ["fotos", "reels", "linkedin", "themen", "marke"] as const;
export type EditionModule = (typeof EDITION_MODULES)[number];

export interface Edition {
  kind: EditionKind;
  modules: EditionModule[];
}

/** Neutral capability: what a client assumes when the field is missing (older server, PLAN §15 O-4). */
export const AGENCY_EDITION: Edition = Object.freeze({ kind: "agency", modules: [] }) as Edition;

export function isEditionModule(v: unknown): v is EditionModule {
  return typeof v === "string" && (EDITION_MODULES as readonly string[]).includes(v);
}

export function isHotelEdition(company: { dfyProfile?: string | null }): boolean {
  return resolveDfyChannel(company).profile === "hotel";
}

/** Comma/whitespace separated module list → known modules only, deduplicated, in EDITION_MODULES order. */
export function parseEditionModules(raw: string | null | undefined): EditionModule[] {
  const wanted = new Set((raw ?? "").split(/[\s,]+/).map((s) => s.trim().toLowerCase()).filter(Boolean));
  return EDITION_MODULES.filter((m) => wanted.has(m));
}

/** Agencies always get no modules; hotels get the enabled modules this code knows. */
export function resolveEdition(
  company: { dfyProfile?: string | null },
  enabledModules: readonly string[],
): Edition {
  if (!isHotelEdition(company)) return { kind: "agency", modules: [] };
  return { kind: "hotel", modules: EDITION_MODULES.filter((m) => enabledModules.includes(m)) };
}

export function hasEditionModule(edition: Edition | null | undefined, module: EditionModule): boolean {
  return !!edition && edition.kind === "hotel" && edition.modules.includes(module);
}
